import ScrollBy from 'components/scrollBy';

// Список плейлистов, где ключ - это id плейлиста, а значение, массив индексов аудио в этом плейлисте
const playersAutoplay = {};

export class AudioPlayer {
    constructor(playerWrapper) {
        /**
         * Список все плееров на странице, нужен для переключения между треками
         * @type {*[]}
         */
        this.players = [];

        /**
         * HTML Обёртка плеера
         */
        this.player = playerWrapper;

        /**
         * Класс показывающий активность какого-либо элемента в плеере
         * @type {string}
         */
        this.activeClass = 'is-active';

        /**
         * Класс показывающии, что аудио загружается
         * @type {string}
         */
        this.loadingClass = 'is-loading';

        /**
         * Порядковый номер текущего плеера
         * @type {number|null}
         */
        this.index = null;

        /**
         * Порядковый номер последнего плеера
         * @type {number|null}
         */
        this.lastIndex = null;

        /**
         * Статус инициализирован ли плеер.
         * По умолчанию у audio вместо src атрибут data-src, который подменяется при инициализации
         * @type {boolean}
         */
        this.isInit = false;

        /**
         * Статус грузится ли сейчас аудио в плеере
         * @type {boolean}
         */
        this.loading = false;

        /**
         * Список доступных статусов, для контроля состояния элементов управления плеером
         * @type {{BUTTON_VOLUME: string, BUTTON_NAVIGATION: string}}
         */
        this.controlStatuses = {
            BUTTON_NAVIGATION: 'navigation',
            BUTTON_VOLUME    : 'volume'
        };

        /**
         * Проигрывается ли этот трек автоматически в плейлисте
         * @type {boolean}
         */
        this.isAutoplay = false;

        /**
         * id плейлиста для автопереключения треков по нему
         * @type {null}
         */
        this.playlistId = null;

        this._bindMethodsContext();
    }

    /**
     * Инициализация компонента плеера
     * @param {HTMLElement[]} players - список всех плееров на странице
     */
    init(players) {
        this.players = players;
        this._setElements(this.player);
        this._playerInit();
        this._bindEvents();
        this._controlsStatus(this.controlStatuses.BUTTON_NAVIGATION);
        this._controlsStatus(this.controlStatuses.BUTTON_VOLUME);
    }

    /**
     * Привязывает контекст экземпляра класса к методам плеера
     * @private
     */
    _bindMethodsContext() {
        this._playStream = this._playStream.bind(this);
        this._nextAudio = this._nextAudio.bind(this);
        this._prevAudio = this._prevAudio.bind(this);
        this._setVolume = this._setVolume.bind(this);
    }

    /**
     * Получает и устанавливает все зависимые html-элементы для работы плеера
     * @param {HTMLElement} player - HTML Обёртка плеера
     * @private
     */
    _setElements(player) {
        this.playButtons = [...player.querySelectorAll('.j-audio-play')];
        this.controlNext = player.querySelector('.j-audio-next');
        this.controlPrev = player.querySelector('.j-audio-prev');
        this.controlVolume = player.querySelector('.j-audio-volume');
        this.progressBar = player.querySelector('.j-audio-progress');
        this.currentTime = player.querySelector('.j-audio-time-current');
        this.fullTime = player.querySelector('.j-audio-time-full');
        this.audioFile = player.querySelector('audio.j-audio-source');
        this.lastIndex = this.players.length - 1;
    }

    /**
     * Инициализация стартовых данных для плеера
     * @private
     */
    _playerInit() {
        this.progressBar.value = 0;
        this.progressBar.disabled = true;
        this.players.forEach((player, index) => {
            player.index = index;
        });

        // Ищем родительский класс обозначающий что треки в этом блоке должны автоматически переключаться
        const autoplayParent = this.player.closest('.j-audio-autoplay');

        if (autoplayParent) {
            // id плейлиста
            this.playlistId = autoplayParent.dataset['id'];

            if (this.playlistId) {
                this.isAutoplay = true;

                // Если плейлист уже инициалирован, то добавляет к нему индекс текущего аудио
                // Иначе создаём новый плейлист
                if (this.playlistId in playersAutoplay) {
                    playersAutoplay[this.playlistId].push(this.index);
                } else {
                    playersAutoplay[this.playlistId] = [this.index];
                }
            }
        }
    }

    /**
     * Навешивает обработчики событий
     * @private
     */
    _bindEvents() {
        if (this.audioFile) {
            // По окончанию трека, ставим его на паузу и переходим к следующему треку в плейлисте
            // если включен автоплей у блока
            this.audioFile.addEventListener('ended', () => {
                this.audioFile.pause();
                this._setPlayPauseClass();

                // Если включен автоплей, задан id плейлиста и плейлист инициализирован
                // запускаем следующую песню из этого плейлиста
                if (this.isAutoplay && this.playlistId && this.playlistId in playersAutoplay) {
                    const playlist = playersAutoplay[this.playlistId];
                    // Ищем индекс текущего трека в плейлисте
                    const currentIndex = playlist.findIndex((item) => {
                        return item === this.index;
                    });

                    // Если индекс аудио не найден или это последнее аудио в плейлисте, выходим
                    if (currentIndex === -1 || currentIndex === playlist.length - 1) {
                        return;
                    }

                    // Запускаем следующий трек из плейлиста
                    this._audioSeek(playlist[currentIndex + 1]);
                }
            });
        }

        if (this.progressBar) {
            this.progressBar.addEventListener('input', () => {
                if (!this.isInit) {
                    return;
                }

                this.audioFile.currentTime = this.progressBar.value;
                this.currentTime.innerHTML = this._getTime(this.audioFile.currentTime);
                this._progressBarChange(this.progressBar);
            });
        }

        this.playButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this._playStream();
            });
        });

        if (this.controlNext) {
            this._addClickListeners(this.controlNext, this._nextAudio);
        }

        if (this.controlPrev) {
            this._addClickListeners(this.controlPrev, this._prevAudio);
        }

        if (this.controlVolume) {
            this._addClickListeners(this.controlVolume, this._setVolume);
        }
    }

    /**
     * Устанавливает доступность элементов контроля плеером
     * @param {string} button - тип элементов
     * @private
     */
    _controlsStatus(button = '') {
        if (button === 'navigation') {
            switch (this.index) {
                case 0:
                    this.controlPrev.setAttribute('data-disabled', true);
                    break;
                case this.lastIndex:
                    this.controlNext.setAttribute('data-disabled', true);
                    break;
                default:
                    this.controlPrev.setAttribute('data-disabled', false);
                    this.controlPrev.setAttribute('data-disabled', false);
                    break;
            }
        }

        if (button === 'volume') {
            this.controlVolume.setAttribute('data-muted', this.audioFile.muted);
        }
    }

    /**
     * Форматирует в секундах время в формат hh:mm:ss
     * @param {number} timeInSec - время в секундах
     * @return {string} - время в формате hh:mm:ss
     * @private
     */
    _getTime(timeInSec) {
        const hours = Math.floor(timeInSec / 60 / 60);
        const minutes = Math.floor(timeInSec / 60) - (hours * 60);
        const seconds = Math.floor(timeInSec % 60);

        if (hours !== 0) {
            return `${this._timeToString(hours)}:${this._timeToString(minutes)}:${this._timeToString(seconds)}`;
        }

        return `${this._timeToString(minutes)}:${this._timeToString(seconds)}`;
    }

    /**
     * Закрашивает прогресс воспрозведения аудио
     * @param {HTMLInputElement} progress - input с прогрессом
     * @private
     */
    _progressBarChange(progress) {
        progress.max = this.audioFile.duration;
        const max = progress.max;
        const val = progress.value;

        progress.style.backgroundSize = `${(val / max) * 100}% 100%`;
    }

    /**
     * Навешивает обработчик события на элемент
     * @param {HTMLElement} button - элемент
     * @param {function(HTMLElement)} callback - событие
     * @private
     */
    _addClickListeners(button, callback) {
        button.addEventListener('click', () => {
            callback(button);
        });
    }

    /**
     * Форматирует часы, минуты или секунды.
     * Добавляет 0 в начало, если значение меньше 10
     * @param {number} time - время
     * @return {string} - отформатированная строка
     * @private
     */
    _timeToString(time) {
        return time >= 10 ? `${time}` : `0${time}`;
    }

    /**
     * Отображает текущие время проигрывания аудио
     * @private
     */
    _getCurrentTime() {
        this.audioFile.addEventListener('timeupdate', () => {
            this.currentTime.innerHTML = this._getTime(this.audioFile.currentTime);
            this.progressBar.value = this.audioFile.currentTime;
            this._progressBarChange(this.progressBar);
        });
    }

    /**
     * Навешивает/убирает на кнопку воспроизведения класс активности
     * Используется для визуального отображения состояния кнопки воспроизведения
     * @private
     */
    _setPlayPauseClass() {
        const method = this.audioFile.paused ? 'remove' : 'add';

        this.playButtons.forEach((playButton) => {
            playButton.classList[method](this.activeClass);
        });
    }

    /**
     * Устанавливает статус загрузки у конкретного плеера
     * @param {AudioPlayer} player - экземпляр плеера
     * @param {boolean} loading - запускаем/останавливаем лоадер
     * @private
     */
    _changeLoading(player, loading) {
        const method = loading ? 'add' : 'remove';

        player.loading = loading;

        player.playerButtonsPlay.forEach((playButton) => {
            playButton.classList[method](this.loadingClass);
        });
    }

    /**
     * Воспроизводит аудио
     * @param {AudioPlayer} player - экземпляр плеера
     * @private
     */
    _playStream(player) {
        const currPlayer = player ? player : this;

        // Если текущий плеер инициализирован, то воспроизводим аудио
        // Иначе инициализируем плеер
        if (currPlayer.isInit) {
            this.players.forEach((playerX) => {
                if (playerX !== currPlayer) {
                    playerX.loading = false;

                    if (!playerX.audioFile.paused) {
                        playerX.audioFile.pause();
                        playerX.playButtons.forEach((playButton) => {
                            playButton.classList.remove(this.activeClass);
                        });
                    }
                }
            });

            if (currPlayer.audioFile.paused) {
                currPlayer.audioFile.play();
            } else {
                currPlayer.audioFile.pause();
                // currPlayer.playButtons.forEach((playButton) => {
                //     playButton.classList.remove(this.activeClass);
                // });
            }

            currPlayer._setPlayPauseClass();
            currPlayer._getCurrentTime();
        } else {
            this._checkInitPlayer(currPlayer);
        }
    }

    /**
     * Инициализирует плеер и начинает подгрузку аудио
     * @param {AudioPlayer} player - экземпляр плеера
     * @private
     */
    _checkInitPlayer(player) {
        if (player.isInit) {
            this._playStream(player);
        } else {
            this._changeLoading(player, true);

            const play = () => {
                player.isInit = true;
                player.progressBar.disabled = false;

                this._changeLoading(player, false);
                this._playStream(player);
            };

            // Когда аудио будет готово к воспроизведению, запускаем его
            // Для айфонов используем другое событие
            if ((/iPad|iPhone|iPod/).test(navigator.userAgent)) {
                player.audioFile.addEventListener('progress', () => {
                    if (!player.isInit) {
                        play();
                    }
                }, {once: true});
            } else {
                player.audioFile.addEventListener('loadeddata', () => {
                    // readyState у audio элемента больше 2-х,
                    // указывает на то что оно готово к воспроизведению
                    if (player.audioFile.readyState >= 2 && !player.isInit) {
                        play();
                    }
                });
            }

            // Подменяем атрибут data-src на src, у audio-элемента
            player.audioFile.src = player.audioFile.dataset['src'];
            player.audioFile.removeAttribute('data-src');
        }
    }

    /**
     * Отключает/включает звук у плеера
     * @private
     */
    _setVolume() {
        this.audioFile.muted = Boolean(!this.audioFile.muted);

        this._controlsStatus(this.controlStatuses.BUTTON_VOLUME);
    }

    /**
     * Запускает воспроизведение аудио по его идексу, из всего списка плееров
     * @param {number} playerID - индекс
     * @param {boolean} isNext - true - если переход к следующему треку, false - если к предыдущему
     * @private
     */
    _audioSeek(playerID, isNext = true) {
        const player = this.players[playerID];

        if (player.isInit && player.audioFile.paused) {
            this._playStream(player);
        } else {
            this._checkInitPlayer(player);
        }

        this._scrollTo(player.player, isNext);
    }

    /**
     * Запускает следующий за текущим плеер и его аудио
     * @private
     */
    _nextAudio() {
        this._audioSeek(this.index + 1, true);
    }

    /**
     * Запускает предыдущий перед текущим плеер и его аудио
     * @private
     */
    _prevAudio() {
        this._audioSeek(this.index - 1, false);
    }

    /**
     * Пролистывает страницу к нужному плееру
     * @param {AudioPlayer} player - экземпляр плеера
     * @param {boolean} isNext - true - если переход к следующему треку, false - если к предыдущему
     * @private
     */
    _scrollTo(player, isNext) {
        const scrollBy = new ScrollBy();
        // Отступ после скролла до элемента
        // если листаем к следующему треку, то отступ меньше, чтобы страница прокручивалась вниз
        // если к предыдущему, то отступ больше, чтобы страница прокручивалась вверх
        const offset = isNext ? 93 : 193;

        scrollBy.init({offset});

        scrollBy.scrollToElement(player);
    }

    /**
     * Возвращает массив кнопок запуска плеера
     * @return {*|*[]} - массив кнопок
     */
    get playerButtonsPlay() {
        return this.playButtons;
    }
}
